/**
 * Analytics and User Tracking Utilities
 * Handles user behavior tracking, lead qualification, and conversion analytics
 */

class Analytics {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userId = this.getUserId();
    this.events = [];
    this.startTime = Date.now();
    this.isTracking = true;
    
    // Initialize analytics
    this.init();
  }

  init() {
    // Track page load
    this.track('page_load', {
      url: window.location.href,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
      timestamp: new Date().toISOString()
    });

    // Track scroll depth
    this.setupScrollTracking();
    
    // Track time on page
    this.setupTimeTracking();
    
    // Track before unload
    window.addEventListener('beforeunload', () => {
      this.track('session_end', {
        duration: Date.now() - this.startTime,
        events_count: this.events.length
      });
      this.flush();
    });
  }

  generateSessionId() {
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  getUserId() {
    let userId = localStorage.getItem('captif_user_id');
    if (!userId) {
      userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('captif_user_id', userId);
    }
    return userId;
  }

  /**
   * Track an event
   * @param {string} event - Event name
   * @param {object} properties - Event properties
   */
  track(event, properties = {}) {
    if (!this.isTracking) return;

    const eventData = {
      event,
      properties: {
        ...properties,
        session_id: this.sessionId,
        user_id: this.userId,
        timestamp: new Date().toISOString(),
        page_url: window.location.href
      }
    };

    this.events.push(eventData);
    
    // Send to backend if events buffer is large
    if (this.events.length >= 10) {
      this.flush();
    }

    console.log('[Analytics]', event, properties);
  }

  /**
   * Track form interactions
   */
  trackFormStart(formType) {
    this.track('form_start', {
      form_type: formType,
      form_step: 1
    });
  }

  trackFormStep(formType, step, data = {}) {
    this.track('form_step', {
      form_type: formType,
      form_step: step,
      ...data
    });
  }

  trackFormComplete(formType, data = {}) {
    this.track('form_complete', {
      form_type: formType,
      completion_time: Date.now() - this.startTime,
      ...data
    });
  }

  trackFormError(formType, error, field = null) {
    this.track('form_error', {
      form_type: formType,
      error_message: error,
      error_field: field
    });
  }

  trackFormAbandonment(formType, step, data = {}) {
    this.track('form_abandon', {
      form_type: formType,
      abandon_step: step,
      time_spent: Date.now() - this.startTime,
      ...data
    });
  }

  /**
   * Lead qualification scoring
   */
  calculateLeadScore(formData) {
    let score = 0;
    
    // Surface area scoring (bigger projects = higher score)
    const surface = parseFloat(formData.surface) || 0;
    if (surface > 500) score += 40;
    else if (surface > 200) score += 30;
    else if (surface > 100) score += 20;
    else if (surface > 50) score += 10;

    // Workplace concept scoring
    const workplaceConcepts = {
      'closed': 30,    // Higher budget typically
      'semi-open': 20,
      'open': 10
    };
    score += workplaceConcepts[formData.workplace_concept] || 0;

    // Finish level scoring
    const finishLevels = {
      'high': 30,
      'medium': 20,
      'basic': 10
    };
    score += finishLevels[formData.finish_level] || 0;

    // Furniture standard scoring
    const furnitureStandards = {
      'high': 25,
      'medium': 15,
      'low': 5
    };
    score += furnitureStandards[formData.furniture_standard] || 0;

    // Sustainable solutions (premium feature)
    if (formData.sustainable_solutions) {
      score += 15;
    }

    // Additional details provided (shows engagement)
    if (formData.additional_info && formData.additional_info.length > 50) {
      score += 10;
    }

    // Floor plan uploaded (high engagement)
    if (formData.floor_plan_file) {
      score += 15;
    }

    // Company email domain scoring
    const email = formData.contact_info?.email || '';
    if (email.includes('@') && !email.includes('gmail') && !email.includes('yahoo') && !email.includes('hotmail')) {
      score += 20; // Business email
    }

    return Math.min(score, 100); // Cap at 100
  }

  getLeadQuality(score) {
    if (score >= 80) return 'hot';
    if (score >= 60) return 'warm';
    if (score >= 40) return 'qualified';
    return 'cold';
  }

  /**
   * Track button clicks and interactions
   */
  trackButtonClick(buttonName, context = {}) {
    this.track('button_click', {
      button_name: buttonName,
      ...context
    });
  }

  trackLanguageChange(fromLang, toLang) {
    this.track('language_change', {
      from_language: fromLang,
      to_language: toLang
    });
  }

  trackCalculationAttempt(formData) {
    const leadScore = this.calculateLeadScore(formData);
    const leadQuality = this.getLeadQuality(leadScore);
    
    this.track('calculation_attempt', {
      surface: formData.surface,
      workplace_concept: formData.workplace_concept,
      finish_level: formData.finish_level,
      furniture_standard: formData.furniture_standard,
      sustainable_solutions: formData.sustainable_solutions,
      has_floor_plan: !!formData.floor_plan_file,
      has_additional_info: !!formData.additional_info,
      lead_score: leadScore,
      lead_quality: leadQuality
    });

    return { leadScore, leadQuality };
  }

  trackCalculationSuccess(result, leadData) {
    this.track('calculation_success', {
      price_per_m2: result.price_per_m2,
      total_budget: result.total_budget,
      surface: result.surface,
      lead_score: leadData.leadScore,
      lead_quality: leadData.leadQuality,
      calculation_time: Date.now() - this.startTime
    });
  }

  /**
   * Scroll depth tracking
   */
  setupScrollTracking() {
    let maxScroll = 0;
    let scrollPoints = [25, 50, 75, 90];
    let trackedPoints = new Set();

    const trackScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      
      maxScroll = Math.max(maxScroll, scrollPercent);
      
      scrollPoints.forEach(point => {
        if (scrollPercent >= point && !trackedPoints.has(point)) {
          trackedPoints.add(point);
          this.track('scroll_depth', {
            depth_percent: point,
            max_depth: maxScroll
          });
        }
      });
    };

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          trackScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /**
   * Time tracking
   */
  setupTimeTracking() {
    const intervals = [30, 60, 120, 300]; // 30s, 1m, 2m, 5m
    const trackedIntervals = new Set();

    setInterval(() => {
      const timeSpent = Math.round((Date.now() - this.startTime) / 1000);
      
      intervals.forEach(interval => {
        if (timeSpent >= interval && !trackedIntervals.has(interval)) {
          trackedIntervals.add(interval);
          this.track('time_on_page', {
            seconds: interval,
            engagement_level: timeSpent > 120 ? 'high' : timeSpent > 60 ? 'medium' : 'low'
          });
        }
      });
    }, 1000);
  }

  /**
   * Send events to backend
   */
  async flush() {
    if (this.events.length === 0) return;

    try {
      await fetch('/api/analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          events: this.events,
          session_id: this.sessionId,
          user_id: this.userId
        })
      });
      
      this.events = []; // Clear events after sending
    } catch (error) {
      console.warn('[Analytics] Failed to send events:', error);
      // Keep events for retry
    }
  }

  /**
   * Get user insights for admin dashboard
   */
  getUserInsights() {
    return {
      session_id: this.sessionId,
      user_id: this.userId,
      events_count: this.events.length,
      time_on_site: Date.now() - this.startTime,
      page_url: window.location.href
    };
  }

  /**
   * Disable tracking (GDPR compliance)
   */
  disable() {
    this.isTracking = false;
    localStorage.setItem('captif_analytics_disabled', 'true');
  }

  /**
   * Enable tracking
   */
  enable() {
    this.isTracking = true;
    localStorage.removeItem('captif_analytics_disabled');
  }

  /**
   * Check if tracking is disabled
   */
  isDisabled() {
    return localStorage.getItem('captif_analytics_disabled') === 'true';
  }
}

// Create singleton instance
const analytics = new Analytics();

// Export tracking functions
export const trackEvent = (event, properties) => analytics.track(event, properties);
export const trackFormStart = (formType) => analytics.trackFormStart(formType);
export const trackFormStep = (formType, step, data) => analytics.trackFormStep(formType, step, data);
export const trackFormComplete = (formType, data) => analytics.trackFormComplete(formType, data);
export const trackFormError = (formType, error, field) => analytics.trackFormError(formType, error, field);
export const trackFormAbandonment = (formType, step, data) => analytics.trackFormAbandonment(formType, step, data);
export const trackButtonClick = (buttonName, context) => analytics.trackButtonClick(buttonName, context);
export const trackLanguageChange = (fromLang, toLang) => analytics.trackLanguageChange(fromLang, toLang);
export const trackCalculationAttempt = (formData) => analytics.trackCalculationAttempt(formData);
export const trackCalculationSuccess = (result, leadData) => analytics.trackCalculationSuccess(result, leadData);
export const calculateLeadScore = (formData) => analytics.calculateLeadScore(formData);
export const getLeadQuality = (score) => analytics.getLeadQuality(score);
export const getUserInsights = () => analytics.getUserInsights();
export const disableTracking = () => analytics.disable();
export const enableTracking = () => analytics.enable();
export const isTrackingDisabled = () => analytics.isDisabled();

export default analytics;